import { createSlice, createAsyncThunk, type PayloadAction } from '@reduxjs/toolkit'
import type { PortfolioState, Investment, PortfolioAllocation } from '@/types'

// Async thunk for fetching investment data
export const fetchInvestmentData = createAsyncThunk(
  'portfolio/fetchInvestmentData',
  async (symbol: string) => {
    const response = await fetch(`https://query1.finance.yahoo.com/v8/finance/chart/${symbol}`)
    const data = await response.json()
    return { symbol, data: data.chart.result[0] }
  }
)

const initialState: PortfolioState = {
  investments: [],
  loading: false,
  error: null,
  totalValue: 0,
  allocation: {
    stocks: 0,
    bonds: 0,
    realEstate: 0,
    cash: 0,
  },
}

const portfolioSlice = createSlice({
  name: 'portfolio',
  initialState,
  reducers: {
    addInvestment: (state, action: PayloadAction<Omit<Investment, 'id'>>) => {
      const investment: Investment = {
        id: Date.now(),
        ...action.payload,
      }
      state.investments.push(investment)
      state.totalValue = state.investments.reduce((sum, inv) => sum + (inv.currentValue || 0), 0)
    },
    updateInvestment: (state, action: PayloadAction<{ index: number; updates: Partial<Investment> }>) => {
      const { index, updates } = action.payload
      if (state.investments[index]) {
        state.investments[index] = { ...state.investments[index], ...updates }
        state.totalValue = state.investments.reduce((sum, inv) => sum + (inv.currentValue || 0), 0)
      }
    },
    removeInvestment: (state, action: PayloadAction<number>) => {
      state.investments.splice(action.payload, 1)
      state.totalValue = state.investments.reduce((sum, inv) => sum + (inv.currentValue || 0), 0)
    },
    updateAllocation: (state, action: PayloadAction<Partial<PortfolioAllocation>>) => {
      const update = action.payload
      if (update.stocks !== undefined) state.allocation.stocks = update.stocks
      if (update.bonds !== undefined) state.allocation.bonds = update.bonds
      if (update.realEstate !== undefined) state.allocation.realEstate = update.realEstate
      if (update.cash !== undefined) state.allocation.cash = update.cash
    },
    setTotalValue: (state, action: PayloadAction<number>) => {
      state.totalValue = action.payload
    },
    setPortfolioData: (state, action: PayloadAction<Partial<PortfolioState>>) => {
      const data = action.payload
      if (data.investments) state.investments = data.investments
      if (data.totalValue !== undefined) state.totalValue = data.totalValue
      if (data.allocation) state.allocation = { ...state.allocation, ...data.allocation }
      if (data.loading !== undefined) state.loading = data.loading
      if (data.error !== undefined) state.error = data.error
    },
    setInvestments: (state, action: PayloadAction<Investment[]>) => {
      state.investments = action.payload
      state.totalValue = state.investments.reduce((sum, inv) => sum + (inv.currentValue || 0), 0)
    },
  },
  extraReducers: (builder) => {
    builder
      .addCase(fetchInvestmentData.pending, (state) => {
        state.loading = true
        state.error = null
      })
      .addCase(fetchInvestmentData.fulfilled, (state, action) => {
        state.loading = false
        const { symbol, data } = action.payload
        const index = state.investments.findIndex((inv) => inv.symbol === symbol)
        if (index !== -1 && data?.meta?.regularMarketPrice) {
          const currentPrice = data.meta.regularMarketPrice
          state.investments[index].currentValue = currentPrice * (state.investments[index].shares || 100)
          state.investments[index].currentPrice = currentPrice
        }
        state.totalValue = state.investments.reduce((sum, inv) => sum + (inv.currentValue || 0), 0)
      })
      .addCase(fetchInvestmentData.rejected, (state, action) => {
        state.loading = false
        state.error = action.error.message || 'Failed to fetch investment data'
      })
  },
})

export const {
  addInvestment,
  updateInvestment,
  removeInvestment,
  updateAllocation,
  setTotalValue,
  setPortfolioData,
  setInvestments,
} = portfolioSlice.actions

export default portfolioSlice.reducer
